import AppContext from 'App/AppContext';
import { IPLabel, IPPrimaryButton, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import HrefLink from 'components/Links/HrefLink';
import { getOutputPurposeTitle } from 'components/OutputLocations/outputLocationTexts';
import SharepointFolderPicker from 'components/SharePoint/SharepointFolderPicker';
import SharepointListPicker from 'components/SharePoint/SharepointListPicker';
import SharepointSitePicker from 'components/SharePoint/SharepointSitePicker';
import { onRenderLabelWithInfo } from 'globalFunctions';
import { globalStackTokensGapMedium, sharepointIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import { ResourceListSystemType } from 'models/resourceList';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDriveItem, IList, ISite } from 'services/Graph/SharepointInterfaces';
import { overflow } from 'utils/string';
import { decodeURIFromHtml, validateURL } from 'utils/url';

interface IAddDocumentLibraryListProps {
  folder?: IDriveItem;
  selectFolder: (item: IDriveItem) => void;
}

interface IAddCustomListProps {
  list?: IList;
  selectList: (item: IList) => void;
}

interface IAddSiteProps {
  site?: ISite;
  selectSite: (item: ISite) => void;
}

interface IAddUrlListProps {
  webUrl: string | undefined;
  setUrl: (newValue: string | undefined) => void;
}

interface IComboChooseSystemListTypeProps {
  /** The saved category, to show the output purposes bound to it; undefined for a new category. */
  listId?: number;
  selectedKey?: ResourceListSystemType;
  onChange: (systemType: ResourceListSystemType) => void;
}

export const ChooseDocumentLibraryList = (props: IAddDocumentLibraryListProps) => {
  const { t } = useTranslation(['lists', 'translation']);
  const [folder, setFolder] = useState<boolean>(false);

  return (
    <IPStack tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPPrimaryButton
          text={t('lists:AddNew.ChooseDocumentLibrary')}
          iconProps={sharepointIcon}
          onClick={() => setFolder(true)}
        />
      </IPStack.Item>
      <IPStack.Item>
        {props.folder && (
          <HrefLink href={props.folder.webUrl ?? undefined} external underline>
            <IPText>{decodeURIFromHtml(overflow(props.folder.webUrl ?? '', 80))}</IPText>
          </HrefLink>
        )}
        {!props.folder && (
          <IPText>
            <i>{t('lists:AddNew.NoneSelected')}</i>
          </IPText>
        )}
      </IPStack.Item>
      {folder && (
        <SharepointFolderPicker
          isOpen={folder}
          onSelect={(item: IDriveItem) => props.selectFolder(item)}
          close={() => setFolder(false)}
        />
      )}
    </IPStack>
  );
};

export const ChooseCustomList = (props: IAddCustomListProps) => {
  const { t } = useTranslation(['lists', 'translation']);
  const [list, setList] = useState<boolean>(false);

  return (
    <IPStack tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPPrimaryButton
          text={t('lists:AddNew.ChooseCustomList')}
          iconProps={sharepointIcon}
          onClick={() => setList(true)}
        />
      </IPStack.Item>
      <IPStack.Item>
        {props.list && (
          <HrefLink href={props.list.webUrl ?? undefined} external underline>
            <IPText>{overflow(props.list.webUrl ?? '', 80)}</IPText>
          </HrefLink>
        )}
        {!props.list && (
          <IPText>
            <i>{t('lists:AddNew.NoneSelected')}</i>
          </IPText>
        )}
      </IPStack.Item>
      {list && (
        <SharepointListPicker
          isOpen={list}
          close={() => setList(false)}
          onSelect={(item: IList) => {
            props.selectList(item);
          }}
        />
      )}
    </IPStack>
  );
};

export const ChooseSite = (props: IAddSiteProps) => {
  const { t } = useTranslation(['lists', 'translation']);
  const [site, setSite] = useState<boolean>(false);

  return (
    <IPStack tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPPrimaryButton
          text={t('lists:AddNew.ChooseSitePageLibrary')}
          iconProps={sharepointIcon}
          onClick={() => setSite(true)}
        />
      </IPStack.Item>
      <IPStack.Item>
        {props.site && (
          <HrefLink href={props.site.webUrl ?? undefined} external underline>
            <IPText>{overflow(props.site.webUrl ?? '', 80)}</IPText>
          </HrefLink>
        )}
        {!props.site && (
          <IPText>
            <i>{t('lists:AddNew.NoneSelected')}</i>
          </IPText>
        )}
      </IPStack.Item>
      {site && (
        <SharepointSitePicker
          isOpen={site}
          close={() => setSite(false)}
          onSelect={(item: ISite) => {
            props.selectSite(item);
          }}
        />
      )}
    </IPStack>
  );
};

export const ChooseUrlList = (props: IAddUrlListProps) => {
  const { t } = useTranslation(['lists']);

  return (
    <IPStack>
      <IPTextField
        onRenderLabel={() => onRenderLabelWithInfo(t('lists:Edit.URL'), t('lists:Edit.URLInfo'))}
        value={props.webUrl ?? ''}
        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
          props.setUrl(newValue);
        }}
        onGetErrorMessage={(value: string) => {
          if (!value || validateURL(value)) return '';
          else return t('lists:AddNew.InvalidURL');
        }}
      />
    </IPStack>
  );
};

export const ComboChooseSystemListType = (props: IComboChooseSystemListTypeProps) => {
  const { t } = useTranslation(['lists', 'outputLocations']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usedLists, setUsedLists] = useState<Set<ResourceListSystemType | undefined>>();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const lists = await appContext.globalDataCache.lists.getItems();
      const usedLists = new Set([...lists.filter((l) => l.systemListId).map((l) => l.systemListId)]);
      setUsedLists(usedLists);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //the output locations are bound in Admin, Settings, Reports, and a category bound there shows the binding instead
  //of the choice
  const boundPurposes = appContext.globalDataCache.outputLocations.getPurposesForList(props.listId);

  const getSystemTypes = (): IPSelectableOption[] => {
    if (isLoading) return [];

    const items: IPSelectableOption[] = [
      {
        key: 0,
        text: t('lists:List.SystemType.None'),
      },
    ];

    //there can be multiple asset lists
    items.push({
      key: ResourceListSystemType.Assets,
      text: t('lists:List.SystemType.Assets'),
    });

    //one threats list
    if (
      !usedLists ||
      props.selectedKey === ResourceListSystemType.Threats ||
      !usedLists.has(ResourceListSystemType.Threats)
    ) {
      items.push({
        key: ResourceListSystemType.Threats,
        text: t('lists:List.SystemType.Threats'),
      });
    }

    return items;
  };

  if (boundPurposes.length > 0) {
    return (
      <IPStack>
        <IPLabel>{t('lists:List.Column.SystemType')}</IPLabel>
        <IPText>
          {t('outputLocations:Category.BoundInAdmin', {
            purpose: boundPurposes
              .map((purpose) => getOutputPurposeTitle(purpose, t as TFunction<string[]>))
              .join(', '),
          })}
        </IPText>
      </IPStack>
    );
  }

  return (
    <IPStack>
      <IPDropdown
        label={t('lists:List.Column.SystemType')}
        selectedKey={props.selectedKey}
        options={getSystemTypes()}
        styles={{ listbox: { maxHeight: 250 } }}
        onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
          props.onChange(option?.key as ResourceListSystemType);
        }}
      />
    </IPStack>
  );
};
